import { computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceAttributesService, WebsocketsEvents, WorkflowsService } from '@api/devices';
import { type DeviceAttribute, type DeviceData, type Workflow, type WorkflowBase, WorkflowStatus } from '@devices/models';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { addEntity, removeAllEntities, removeEntity, setAllEntities, updateEntity, withEntities } from '@ngrx/signals/entities';
import { SocketEvents } from '@ngrx-store';
import { firstValueFrom, map } from 'rxjs';

import { WorkflowService } from './workflow.service';

interface WORKFLOW_TRIGGERED_EVENT {
  code: string;
  deviceId: string;
  timestamp: string;
  value: string;
  workflow: Workflow;
}

interface WorkflowState {
  attributes: DeviceAttribute[];
  device: DeviceData | null;
  isDeleting: boolean;
  isLoading: boolean;
  isUpdatingOrCreating: boolean;
  twoWayCommunication: boolean;
}

export const WorkflowsStore = signalStore(
  withEntities<Workflow>(),
  withState<WorkflowState>({
    attributes: [],
    device: null,
    isDeleting: false,
    isLoading: false,
    isUpdatingOrCreating: false,
    twoWayCommunication: false,
  }),
  withComputed((store) => ({
    workflowRenderList: computed(() => {
      const workflows = store.entities();
      const attributes = store.attributes();
      const device = store.device();
      const twoWayCommunication = store.twoWayCommunication();

      const sortedWorkflows = [...workflows].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      return sortedWorkflows.map(workflow => {
        const attribute = attributes.find(attribute => attribute.name === workflow.code);
        const mergedActionList = [...device?.actionPoints || [], ...device?.deviceRemoteActions || []];
        const isAvailableActionPoint = mergedActionList.some(actionPoint => actionPoint.name === workflow.actionPointName);

        let status: WorkflowStatus;

        if (!twoWayCommunication) {
          status = WorkflowStatus.TWO_WAY_COMMUNICATION_DISABLED;
        } else if (
          workflow.currentAttempts > 0 &&
          workflow.currentAttempts < workflow.maxAttempts &&
          workflow.status === WorkflowStatus.ACTIVE
        ) {
          status = WorkflowStatus.ATTEMPT_RUNNING;
        } else {
          status = workflow.status;
        }

        return {
          ...workflow,
          isAvailableActionPoint,
          status,
          unit: attribute && attribute.measurementUnit,
        };
      });
    }),
  })),
  withMethods((
    store,
    workflowService = inject(WorkflowService),
    deviceAttributeService = inject(DeviceAttributesService),
    workflowsApi = inject(WorkflowsService),
  ) => ({
    async createWorkflow(body: WorkflowBase) {
      const device = store.device();

      if (!device) {
        return Promise.resolve(null);
      }

      patchState(store, { isUpdatingOrCreating: true });

      try {
        const workflow = await workflowService.createWorkflow(device.id, body);

        patchState(store, addEntity(workflow));
        const { data } = await workflowService.getWorkflows(device.id);

        patchState(store, setAllEntities(data));

        return workflow;
      } catch (e) {
        throw e;
      } finally {
        patchState(store, { isUpdatingOrCreating: false });
      }
    },
    async deleteWorkflow(workflow: Workflow) {
      const device = store.device();

      if (!device || !workflow) {
        return Promise.resolve(false);
      }

      patchState(store, { isDeleting: true });

      try {
        await workflowService.deleteWorkflow(device.id, workflow.id);
        patchState(store, removeEntity(workflow.id));

        return true;
      } catch (e) {
        throw e;
      } finally {
        patchState(store, { isDeleting: false });
      }
    },
    fetchDataPointList() {
      deviceAttributeService
        .getAttributes<{ data: DeviceAttribute[]; }>({})
        .pipe(map(response => response.data))
        .subscribe(attributes => {
          patchState(store, { attributes });
        });
    },
    async fetchWorkflows() {
      const device = store.device();

      if (!device) {
        return;
      }

      patchState(store, { isLoading: true });

      try {
        const { data } = await workflowService.getWorkflows(device.id);

        patchState(store, setAllEntities(data));
      } finally {
        patchState(store, { isLoading: false });
      }
    },
    patchWorkflow(workflow: Workflow) {
      if (store.ids().includes(workflow.id)) {
        patchState(store, updateEntity({ changes: workflow, id: workflow.id }));
      }
    },
    async resetWorkflow(workflowId: string) {
      const device = store.device();

      if (!device) {
        return null;
      }

      patchState(store, { isUpdatingOrCreating: true });

      try {
        const { data } = await firstValueFrom(
          workflowsApi.updateWorkflow<{ data: Workflow; }>({ deviceId: device.id, id: workflowId }, { reactivate: true }),
        );

        patchState(store, updateEntity({ changes: data, id: workflowId }));

        return data;
      } finally {
        patchState(store, { isUpdatingOrCreating: false });
      }
    },
    setDevice(device: DeviceData) {
      if (store.device()?.id === device.id) {
        patchState(store, { device });

        return;
      }
      patchState(store, { device }, removeAllEntities());
    },
    setTwoWayCommunication(twoWayCommunication: boolean) {
      patchState(store, { twoWayCommunication });
    },
    async updateWorkflow(workflowId: string, body: Partial<Workflow>) {
      const device = store.device();

      if (!device) {
        return Promise.resolve(null);
      }

      patchState(store, { isUpdatingOrCreating: true });

      try {
        const workflow = await workflowService.updateWorkflow({ body, deviceId: device.id, workflowId });

        patchState(store, updateEntity({ changes: workflow, id: workflowId }));

        return workflow;
      } catch (e) {
        throw e;
      } finally {
        patchState(store, { isUpdatingOrCreating: false });
      }
    },
  })),
  withHooks({
    onInit(store) {
      effect(async () => {
        if (store.device()) {
          await store.fetchWorkflows();
        }
      });
      store.fetchDataPointList();

      SocketEvents
        .pipe(
          takeUntilDestroyed(),
        )
        .subscribe(({ data, type }) => {
          switch (type) {
            case WebsocketsEvents.WORKFLOW_ATTEMPTS_UPDATED:
            case WebsocketsEvents.WORKFLOW_STATUS_CHANGED:
              store.patchWorkflow(data as Workflow);
              break;
            case WebsocketsEvents.WORKFLOW_TRIGGERED:
              store.patchWorkflow((data as WORKFLOW_TRIGGERED_EVENT).workflow);
              break;
            default:
              return;
          }
        });
    },
  }),
);
